import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { platformSections, trackedSectionIds, sectionColors } from '../data/navigation';
import { scrollToSection } from '../utils/scroll';
export function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isPricing = location.pathname === '/pricing';
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [platformMenuOpen, setPlatformMenuOpen] = useState(false);
  const [mobilePlatformOpen, setMobilePlatformOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('home');
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      if (!isHome) return;
      // Determine which section is currently in view
      for (const sectionId of trackedSectionIds) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 300 && rect.bottom >= 300) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  // Sections live on the marketing page, so navigate home first when elsewhere
  const goToSection = (sectionId: string) => {
    setMobileMenuOpen(false);
    setPlatformMenuOpen(false);
    if (!isHome) {
      navigate('/', {
        state: {
          scrollTo: sectionId
        }
      });
      return;
    }
    scrollToSection(sectionId);
  };
  const isSectionActive = (sectionId: string) => isHome && activeSection === sectionId;
  const navItemClass = (active: boolean) => active ? 'border-2 text-white' : 'text-gray-300 hover:text-white hover:bg-white/5 border-2 border-transparent';
  const navItemStyle = (active: boolean, sectionId: string) => active ? {
    borderColor: sectionColors[sectionId] || '#E9A165'
  } : {};
  const platformActive = isHome && platformSections.some(item => item.id === activeSection);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800 shadow-2xl' : 'bg-transparent'}`} data-id="element-89" data-mp="19uasy5f5tr8i">
      <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-90" data-mp="w0jsdmjephox">
        <div className="flex items-center justify-between" data-id="element-91" data-mp="w1bb3wrwnkxs">
          {/* Logo */}
          <button onClick={() => goToSection('home')} className="flex items-center space-x-3 group" aria-label="Ari OS home" data-id="element-92" data-mp="r1736v52byj6">
            <img src="https://cdn.magicpatterns.com/uploads/3vCu7oKxcfkEiVUzGfZBCk/Ari_Logo_White.png" alt="Ari OS" className="h-8 group-hover:scale-110 transition-transform" data-id="element-93" data-mp="22gvsht5sh9n5" />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2" data-id="element-94" data-mp="20zgdbxr9zpl2">
            <button onClick={() => goToSection('home')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${navItemClass(isSectionActive('home'))}`} style={navItemStyle(isSectionActive('home'), 'home')} data-id="element-95" data-mp="2119l5gdmkfun">
              Home
            </button>

            {/* Ari OS — grouped dropdown */}
            <div className="relative" onMouseEnter={() => setPlatformMenuOpen(true)} onMouseLeave={() => setPlatformMenuOpen(false)} data-id="element-96" data-mp="2jz40sd99dpm0">
              <button onClick={() => goToSection('ari-os')} onFocus={() => setPlatformMenuOpen(true)} aria-haspopup="true" aria-expanded={platformMenuOpen} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg transition-all text-sm font-medium ${navItemClass(platformActive)}`} style={navItemStyle(platformActive, activeSection)} data-id="element-97" data-mp="3boyqwc4xxjbq">
                Ari OS
                <ChevronDownIcon size={14} className={`transition-transform duration-200 ${platformMenuOpen ? 'rotate-180' : ''}`} data-id="element-98" data-mp="1ivivu8pc4wf9" />
              </button>

              {platformMenuOpen && <div className="absolute left-0 top-full pt-3 w-52" data-id="element-99" data-mp="26kbn3rv0ig3j">
                  <div className="bg-zinc-950/95 backdrop-blur-xl border border-zinc-800 rounded-xl shadow-2xl p-1.5" data-id="element-100" data-mp="21sboq3ulbv92">
                    {platformSections.map(item => <button key={item.id} onClick={() => goToSection(item.id)} className={`w-full px-4 py-2.5 rounded-lg text-left text-sm font-medium transition-colors ${isSectionActive(item.id) ? 'text-white bg-white/[0.07]' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`} data-id="element-101" data-mp="1xp5mr8eigq8e">
                        {item.label}
                      </button>)}
                  </div>
                </div>}
            </div>

            <button onClick={() => goToSection('architecture')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${navItemClass(isSectionActive('architecture'))}`} style={navItemStyle(isSectionActive('architecture'), 'architecture')} data-id="element-102" data-mp="q0cf1w5ui2l8">
              Architecture
            </button>
            <button onClick={() => goToSection('developers')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${navItemClass(isSectionActive('developers'))}`} style={navItemStyle(isSectionActive('developers'), 'developers')} data-id="element-103" data-mp="18prg3qlj6rpm">
              Developers
            </button>
            <Link to="/pricing" className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${navItemClass(isPricing)}`} style={navItemStyle(isPricing, 'pricing')} data-id="element-104" data-mp="3es4tpcx9libg">
              Pricing
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3" data-id="element-105" data-mp="3jolmikfeskuv">
            <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-lg hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all text-sm font-semibold" data-id="element-106" data-mp="1bfer01cpcwd">
              Try Ari
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} data-id="element-107" data-mp="2d29ipno2f0ff">
            {mobileMenuOpen ? <XIcon size={24} data-id="element-108" data-mp="slrw2hjgye80" /> : <MenuIcon size={24} data-id="element-109" data-mp="duswtx4ix213" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4 space-y-2 bg-zinc-950/95 backdrop-blur-xl rounded-b-2xl" data-id="element-110" data-mp="it42dvnscqdi">
            <button onClick={() => goToSection('home')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${navItemClass(isSectionActive('home'))}`} style={navItemStyle(isSectionActive('home'), 'home')} data-id="element-111" data-mp="4stacpaqp5qr">
              Home
            </button>

            {/* Ari OS — grouped, collapsible */}
            <div data-id="element-112" data-mp="1pb9abkwip8lb">
              <button onClick={() => setMobilePlatformOpen(!mobilePlatformOpen)} aria-expanded={mobilePlatformOpen} className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all text-sm font-medium ${navItemClass(platformActive)}`} style={navItemStyle(platformActive, activeSection)} data-id="element-113" data-mp="1parbcgih0g9p">
                <span data-id="element-114" data-mp="881mregeppaj">Ari OS</span>
                <ChevronDownIcon size={16} className={`transition-transform duration-200 ${mobilePlatformOpen ? 'rotate-180' : ''}`} data-id="element-115" data-mp="3at1v63vj75l" />
              </button>

              {mobilePlatformOpen && <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-1" data-id="element-116" data-mp="3qepmspwlnm9c">
                  {platformSections.map(item => <button key={item.id} onClick={() => goToSection(item.id)} className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-colors ${isSectionActive(item.id) ? 'text-white bg-white/[0.07]' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-id="element-117" data-mp="3li908051tfyd">
                      {item.label}
                    </button>)}
                </div>}
            </div>

            <button onClick={() => goToSection('architecture')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${navItemClass(isSectionActive('architecture'))}`} style={navItemStyle(isSectionActive('architecture'), 'architecture')} data-id="element-118" data-mp="1r67nio8kkpa4">
              Architecture
            </button>
            <button onClick={() => goToSection('developers')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${navItemClass(isSectionActive('developers'))}`} style={navItemStyle(isSectionActive('developers'), 'developers')} data-id="element-119" data-mp="189ycyawvmguv">
              Developers
            </button>
            <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className={`block w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${navItemClass(isPricing)}`} style={navItemStyle(isPricing, 'pricing')} data-id="element-120" data-mp="2tm9itliwbwv2">
              Pricing
            </Link>

            <div className="pt-4 space-y-2" data-id="element-121" data-mp="2sqi8xmecrkdt">
              <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="w-full px-4 py-3 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-lg hover:shadow-xl transition-all text-sm font-semibold block text-center" data-id="element-122" data-mp="2xnruus2j9dww">
                Try Ari
              </a>
            </div>
          </div>}
      </div>
    </header>;
}